import React, { FC, forwardRef, ReactNode } from 'react'
import useTranslation from 'next-translate/useTranslation'
import { Divider, FlexboxGrid } from 'rsuite'
import { useFeatureIsOn, FeaturesReady } from '@growthbook/growthbook-react'

import DeliveryTerms from './DeliveryTerms'
import StandardsAndLabels from './StandardsAndLabels'
import UseCases from './UseCases'
import FeaturesList from './FeaturesList'
import useIsOnMobile from '../../../services/useIsOnMobile'
import { Product, ProductNavigationRefs, Supplier } from '../../../utils/types/Product'
import { ScreenSize } from '../../../utils/constants'
import { getFeatureTypesArray, getOrderedProductCategories, getProductDescription, getProductMedia } from '../../../lib/product'
import FreeSamplePromotion from '../FreeSamplePromotion'
import DocumentRequestButton from '../../Buttons/DocumentRequestButton'
import PageIcon from '../../Icons/PageIcon'
import InfoIcon from '../../Icons/InfoIcon'
import useSingleProductCategoryContent from '../../../services/strapi/useSingleProductCategoryContent'
import ReadMoreWrapper from '../../ReadMoreWrapper'
import ProductDocumentsList from './ProductDocumentsList'
import CompatibleProducts from '../CompatibleProducts'

import styles from '../../../styles/ProductAttributes.module.less'

interface ProductAttributesProps {
  product: Product
  supplier?: Supplier
  productNavigationRefs: ProductNavigationRefs
}
interface AttributeSegmentProps {
  headline: string
  children?: ReactNode
  className?: string
}

const AttributeSegment = forwardRef< HTMLDivElement, AttributeSegmentProps>(
  (props, ref) => {
    const {
      children,
      className,
      headline,
    } = props

    const isOnMobile = useIsOnMobile(ScreenSize.md)

    return (
      <FlexboxGrid
        className={`${className} ${styles['attribute-segment']}`}
        ref={ref}
      >
        <FlexboxGrid.Item colspan={!isOnMobile ? 8 : undefined}>
          <h2 className="h4">{headline}</h2>
        </FlexboxGrid.Item>
        <FlexboxGrid.Item colspan={!isOnMobile ? 16 : undefined}>
          {children}
        </FlexboxGrid.Item>
      </FlexboxGrid>
    )
  },
)

const ProductAttributes: FC<ProductAttributesProps> = (props) => {
  const { product, supplier, productNavigationRefs } = props

  const { t } = useTranslation('products')
  const featuretypes = getFeatureTypesArray(product)
  const isOnMobile = useIsOnMobile(ScreenSize.md)
  const showProductDescriptiontFeatureFlag = useFeatureIsOn('spp-show-product-description')
  const showCategoryFourDescription = useFeatureIsOn('show-category-four-description-on-spp')
  const showTechnicalDocumentsInline = useFeatureIsOn('spp-show-techincal-documents-inline')
  const enableRelatedProducts = useFeatureIsOn('enable-related-products')
  const productDescription = getProductDescription(product) || t('The product description has not been specified')

  const orderedCategories = getOrderedProductCategories(product)
  const categoryFour = product.catalog.find((cat) => cat?.['catalog.level'] === 4)
  const hasCompatibleProducts = (product.accessoryProducts?.length ?? 0) > 0
                                || (product.replacementProducts?.length ?? 0) > 0

  const singleProductCategoryContent = useSingleProductCategoryContent(
    // Use level two category
    orderedCategories?.[1]?.['catalog.code'],
  )

  const {
    productFeaturesRef,
    productStandardsRef,
    deliveryTermsRef,
    productDocumentsRef,
  } = productNavigationRefs

  const media = product ? getProductMedia(product) : []

  const productDescriptionJsx = productDescription?.split('\n').map((paragraph) => {
    if (!paragraph) {
      // Add extra linebreaks if multiple consecutive linebreaks
      return <br />
    }
    return <p key={paragraph}>{paragraph}</p>
  })

  const productDescriptionWrapped = (
    <ReadMoreWrapper className="padding-top-spacer-double padding-left-spacer-double margin-left-spacer-half">
      {productDescriptionJsx}
    </ReadMoreWrapper>
  )

  return (
    <div className={styles['product-attributes']}>
      {featuretypes.length > 0 ? (
        <AttributeSegment
          className={styles.features}
          headline={t('Product description')}
          ref={productFeaturesRef}
        >
          {singleProductCategoryContent && (
            <div className={styles['product-description-content']}>
              {singleProductCategoryContent}
            </div>
          )}

          {/* Use FeaturesReady fallback so description is always rendered in SSG HTML */}
          <FeaturesReady fallback={productDescriptionJsx}>
            {showProductDescriptiontFeatureFlag && productDescriptionWrapped}
          </FeaturesReady>

          {(singleProductCategoryContent || showProductDescriptiontFeatureFlag) && <Divider />}

          <FeaturesList features={featuretypes} />
          {showCategoryFourDescription && !!categoryFour?.['text.long'] && (
            <div className={`${styles['product-description-content']} margin-top-spacer-quadruple`}>
              <h4 className={styles['product-description-content-heading']}>{`${t('About')} ${categoryFour?.['catalog.label']}`}</h4>
              <p className="margin-bottom-spacer">{categoryFour['text.long']}</p>
            </div>
          )}
          {!isOnMobile && <Divider />}
          <UseCases product={product} />
          {product && (
          <FreeSamplePromotion
            product={product}
            media={media}
          />
          )}
        </AttributeSegment>
      )
        : (
          <AttributeSegment
            className={styles.features}
            headline={t('Product description')}
            ref={productFeaturesRef}
          >
            {singleProductCategoryContent && (
              <div className="margin-bottom-spacer-double">
                {singleProductCategoryContent}
              </div>
            )}

            {/* Use FeaturesReady fallback so description is always rendered in SSG HTML */}
            <FeaturesReady fallback={productDescriptionJsx}>
              {showProductDescriptiontFeatureFlag && productDescriptionWrapped}
            </FeaturesReady>

            {product && (
              <FreeSamplePromotion
                product={product}
                media={media}
              />
            )}

          </AttributeSegment>
        )}

      {enableRelatedProducts && hasCompatibleProducts && (
      <>
        <Divider />
        <AttributeSegment
          className="compatible-products"
          headline={t('Compatible products & accessories')}
        >
          <CompatibleProducts product={product} />
        </AttributeSegment>
      </>
      )}

      <Divider />

      <AttributeSegment
        className="standards-and-labels"
        headline={t('Standards and labels')}
        ref={productStandardsRef}
      >
        <StandardsAndLabels product={product} />
        <div className="margin-top-spacer-tripple">
          {!showTechnicalDocumentsInline && (
            <DocumentRequestButton
              product={product}
              Icon={PageIcon}
              text="Request technical documentation"
            />
          )}
          <DocumentRequestButton
            product={product}
            Icon={InfoIcon}
            text={t('Request missing details')}
          />
        </div>
      </AttributeSegment>
      {showTechnicalDocumentsInline && (
        <>
          <Divider />
          <AttributeSegment
            headline={t('Product documents')}
            ref={productDocumentsRef}
          >
            <ProductDocumentsList
              product={product}
            />
          </AttributeSegment>
        </>
      )}
      <Divider />
      <AttributeSegment
        className="supplier-delivery-terms"
        headline={t('{{supplier}} delivery terms', { supplier: supplier?.['supplier.label'] })}
        ref={deliveryTermsRef}
      >
        <DeliveryTerms
          product={product}
          supplier={supplier}
        />
      </AttributeSegment>
    </div>
  )
}

export default ProductAttributes
